import { useRef } from 'react';
import type {
  IframePoolItem,
  IframePoolOfferOdds,
  JustPulledItem,
  PoolOfferDetail,
  PoolOfferListItem,
  TierBadge,
  VolatilityLevel,
} from '../../../modules/api';
import { HelpLink } from '../../../modules/help';
import { JustPulled } from '../../../modules/justPulled';
import { PotentialHits } from '../../../modules/potentialHits';
import { Hits } from '../../../modules/design-system';
import type { CategoryPillItem } from '../../../modules/design-system';
import { DemoRipButton } from '../../../modules/demo';
import { FairnessTicketKeeper } from '../../../modules/fairness/ui';
import { BottomAction, MOBILE_RAIL_SPACER_HEIGHT } from './BottomAction';
import { isOfferAvailable, statusLabel } from './offerStatus';
import { OddsPanel, OddsPanelSkeleton } from './OddsPanel';
import { PackInfoCard } from './PackInfoCard';
import { Slider } from './Slider';

/**
 * Grid layout for the modern pack-detail page.
 *
 * Mirrors the classic grid exactly (same container breakpoints, same slot
 * order) so future iterations only need to swap stub blocks for real
 * components — no surrounding-layout churn.
 *
 *   < 800px  → single column, `action` block at the end of the flow
 *   ≥ 800px  → two columns (hero | 360px sidebar); `action` moves into
 *              the sidebar.
 */

export interface PackLayoutProps {
  /** Currently-centered offer's detail — drives info card, odds, RIP price. */
  offer: PoolOfferDetail | null;
  /** Every pack in the active category — drives the slider slots. */
  categoryOffers: PoolOfferListItem[];
  /** Active offer id (slider centre). */
  activeOfferId: number | null;
  odds: IframePoolOfferOdds | null;
  maxForPurchaseCount: number | undefined;
  potentialHits: IframePoolItem[];
  potentialHitsLoading: boolean;
  justPulledItems: JustPulledItem[];
  justPulledLoading: boolean;
  onShowJustPulled?: () => void;
  categories: CategoryPillItem[];
  activeCategoryId?: string;
  /** Header pill click — updates URL + active gameId. */
  onCategorySelect: (id: string) => void;
  /** Slider swipe — updates active offer in-page, keeps URL stable. */
  onSlideChange?: (offerId: number) => void;
  onTierSelect?: (poolOfferId: number) => void;
  quantity?: number;
  onQuantityChange?: (next: number) => void;
  onBuy?: () => void;
  unitPrice?: number | string;
  volatility: VolatilityLevel;
  onVolatilityChange: (level: VolatilityLevel) => void;
  /** Free demo rip — `pack.demoMode` AND an embed_key in hand. */
  showDemo?: boolean;
  onOpenDemo?: () => void;
}

export function PackLayout({
  offer,
  categoryOffers,
  activeOfferId,
  odds,
  maxForPurchaseCount,
  potentialHits,
  potentialHitsLoading,
  justPulledItems,
  justPulledLoading,
  onShowJustPulled,
  categories: _categories,
  activeCategoryId: _activeCategoryId,
  onCategorySelect: _onCategorySelect,
  onSlideChange,
  quantity = 1,
  onQuantityChange,
  onBuy,
  unitPrice = 0,
  volatility,
  onVolatilityChange,
  showDemo = false,
  onOpenDemo,
}: PackLayoutProps) {
  // Not purchasable (e.g. restocking): the action bar stays, locked, and
  // reads the status instead of the price.
  const unavailableLabel =
    offer && !isOfferAvailable(offer.status) ? statusLabel(offer.status) : undefined;
  // Mobile "Potential Hits" header scrolls down to the actual section below.
  const potentialHitsRef = useRef<HTMLDivElement>(null);

  // Convert category-offers list to TierBadge[] so PackInfoCard's tier
  // selector can render one card per sibling pack. Clicking a card
  // fires `onSlideChange` (same handler the slider's swipe uses) so the
  // slider animates to that offer — and PackInfoCard, OddsPanel,
  // BottomAction all follow because they read from `activeOfferId`.
  //
  // Tier label = the offer's own `badgeName` (per-operator override aware,
  // flat field from ShortSerializer). The `suitableBadges` own-row lookup
  // stays as a fallback for older backends without the flat field; when
  // neither yields a name we fall back to the pack title so the card never
  // shows blank.
  const categoryOffersAsTiers: TierBadge[] = categoryOffers.map((o) => {
    const ownBadge =
      o.suitableBadges.find((b) => b.poolOfferId === o.id) ??
      o.suitableBadges.find((b) => b.isSelf);
    return {
      poolOfferId: o.id,
      name: o.badgeName || ownBadge?.name?.trim() || o.title,
      price: o.price,
      imageUrl: o.packImageUrl,
      subImageUrl: null,
      isSelf: activeOfferId !== null && o.id === activeOfferId,
    };
  });

  // Slider needs at least the current pack to render. When the category
  // index endpoint returns 0 offers (e.g. operator-scope filter excludes
  // siblings, or the URL `game_id` doesn't sit in any visible category),
  // synthesise a single-item list from `offer` so the centre pack still
  // shows. Slider already gates chevrons/dots/drag on `len > 1`, so the
  // synthesised single-item case renders as a static centred pack — no
  // controls, no peeks.
  const sliderOffers: PoolOfferListItem[] =
    categoryOffers.length > 0
      ? categoryOffers
      : offer
        ? [
            {
              id: offer.id,
              title: offer.title,
              price: offer.price,
              status: offer.status,
              isMultiple: false,
              packImageUrl: offer.packImageUrl,
              packImageAspect: offer.packImageAspect,
              packLogoImageUrl: offer.packLogoImageUrl,
              pedestalImageUrl: offer.pedestalImageUrl,
              suitableBadges: offer.suitableBadges,
              // Detail payload has no flat badge field — the synthesised
              // single slide never renders a tier row anyway.
              badgeName: null,
              operatorCurrency: offer.operatorCurrency,
              exchangeRate: offer.exchangeRate,
              demoMode: offer.demoMode,
            },
          ]
        : [];

  return (
    // `@container` lives on a wrapper with NO padding so its content-box
    // size (which is what `@container size` queries against) matches the
    // viewport. If we put `px-X` on the same element, the padding
    // shrinks the content-box and rules like
    // `@[800px]:grid-cols-...` stop firing until viewport ≈ 848 (800 +
    // 48 of padding) — a Tailwind 4 / CSS Container-Query gotcha.
    <div className="@container">
      <div
        className="
          relative pb-8 @[600px]:px-6 @[900px]:px-8
          mx-auto max-w-350
          grid grid-cols-1 gap-2
          @[800px]:grid-cols-[minmax(0,1fr)_360px]
          @[800px]:gap-x-6 @[800px]:gap-y-2
          @[1200px]:grid-cols-[minmax(0,1fr)_400px]
        "
      >
        {/* MOBILE-ONLY demo entry (Figma mobile ref) — top-left under the
            sticky Header, floating OVER the slider/pack art rather than
            taking a row, so toggling demo mode never shifts the page. The
            left offset matches the grid gutter (20px, +24px padding from
            600px). z-30 clears the slider's swipe layer (z-25) so the tap
            lands on the button. Desktop shows the same button lower,
            inside the sidebar (see below) — this one is hidden there. */}
        {showDemo && onOpenDemo && (
          <div className="@[800px]:hidden absolute top-0 left-5 @[600px]:left-11 z-30">
            <DemoRipButton onClick={onOpenDemo} />
          </div>
        )}

        {/* HERO / SLIDER — left column, row 1.
            On tablet (600–800, single column) the slider bleeds BOTH
            sides of the grid's px-6 so the peek packs get clipped by the
            viewport edge — same edge-cut look as mobile — instead of a
            hard cut 24px inside the page. On desktop it bleeds left only
            (px-6 at 800–900, px-8 above) so it runs from the viewport's
            left edge to the sidebar gap, matching Figma 543:2118 where
            the slider area starts flush against the frame's left edge.
            Width is grown by the same amount so the right edge stays
            where the grid put it. */}
        <div
          className="
            relative
            @[600px]:-mx-6 @[600px]:w-[calc(100%+3rem)]
            @[800px]:col-start-1 @[800px]:row-start-1
            @[800px]:mr-0 @[800px]:-ml-6 @[800px]:w-[calc(100%+1.5rem)]
            @[900px]:-ml-8 @[900px]:w-[calc(100%+2rem)]
          "
        >
          <Slider
            offers={sliderOffers}
            activeOfferId={activeOfferId}
            {...(onSlideChange ? { onSlideChange } : {})}
          />
        </div>

        {/* MOBILE-ONLY PotentialHits header (Figma 629:588).
            Replaces the slider's own dot strip on mobile — sits between
            the slider and the OddsPanel, ABOVE the sidebar block. Dots
            reflect `activeOfferId` (one per offer in the current
            category). Tapping a dot fires the same `onSlideChange` the
            slider's swipe / tier-card click uses, so all three stay in
            sync. */}
        {/* `px-5` (= 20 px) on the header matches the inner `left-5
            right-5` positioning of OddsPanel's "Min Value" / "Big Win
            Chance" text — so the label here lines up vertically with
            the leftmost column of OddsPanel and the dots end on the
            same column as "Big Win Chance". */}
        <div className="@[800px]:hidden flex items-center justify-between gap-3 px-5 pt-2 pb-3">
          {/* Label + stacked-cards icon (Figma 702:4741). Tapping it
              smooth-scrolls down to the Potential Hits section below. */}
          <button
            type="button"
            onClick={() =>
              potentialHitsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            aria-label="Scroll to Potential Hits"
            className="flex items-center gap-[7px] cursor-pointer text-white"
          >
            <span className="text-[15px] leading-[23px] font-semibold whitespace-nowrap">
              Potential Hits
            </span>
            <Hits size={22} aria-hidden className="shrink-0 opacity-50" />
          </button>
          {categoryOffers.length > 1 && (
            <div
              data-co-component="SliderDots"
              className="flex items-center gap-[7px]"
            >
              {categoryOffers.map((o) => {
                const active = activeOfferId !== null && o.id === activeOfferId;
                return (
                  <button
                    key={o.id}
                    type="button"
                    aria-label={`Go to ${o.title}`}
                    aria-current={active || undefined}
                    onClick={() => onSlideChange?.(o.id)}
                    className={
                      'rounded-full transition-all cursor-pointer ' +
                      (active ? 'h-2 w-2' : 'h-[6px] w-[6px]')
                    }
                    // Colour flows through `--co-SliderDots-{active,idle}` so
                    // the Design Constructor (SliderDots component) recolours
                    // every dot at once. Fallbacks reproduce the original
                    // `bg-white` / `bg-white/40` Tailwind tones — visual
                    // parity for unmodified deploys.
                    style={{
                      background: active
                        ? 'var(--co-SliderDots-active, rgba(255,255,255,1))'
                        : 'var(--co-SliderDots-idle, rgba(255,255,255,0.4))',
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* SIDEBAR — right column. On desktop a peek pack from the
            slider may slide *behind* the sidebar (no hard clip at the
            slider edge), so the sidebar is given a higher z-index and
            `relative` to overlay any peek silhouette that bleeds into
            its column. The cards themselves are GLASS_SURFACE-blurred,
            so the peek reads as a soft shape under the sidebar.
            z-30 sits below the sticky header (z-40). */}
        {/* `row-span-3` on desktop makes the sidebar cover slider/JustPulled/PotentialHits
            rows of the left column, so the left column stacks tight (no empty gap below
            the slider) when the sidebar is taller than the slider. Mobile keeps single-column
            flow — row-span only activates ≥ 800px. */}
        <aside
          className="
            flex flex-col gap-2
            @[800px]:col-start-2 @[800px]:row-start-1 @[800px]:row-span-3
            @[800px]:relative @[800px]:z-30
          "
        >
          <PackInfoCard
            title={offer?.title ?? ''}
            tiers={categoryOffersAsTiers}
            packLogoImageUrl={offer?.packLogoImageUrl ?? null}
            description={offer?.description ?? null}
            {...(offer ? { buybackPercent: offer.buybackPercent } : {})}
            {...(onSlideChange ? { onTierSelect: onSlideChange } : {})}
          />

          {odds ? (
            <OddsPanel odds={odds} volatility={volatility} onVolatilityChange={onVolatilityChange} />
          ) : (
            <OddsPanelSkeleton />
          )}
          <FairnessTicketKeeper offerId={offer?.id} />

          {/* Desktop demo entry — between odds and the RIP action, matching
              the sidebar ordering in the Figma reference. Mobile shows the
              same button up near the Header instead (see above). */}
          {showDemo && onOpenDemo && (
            <div className="hidden @[800px]:block">
              <DemoRipButton onClick={onOpenDemo} fullWidth />
            </div>
          )}

          <div className="hidden @[800px]:block">
            <BottomAction
              quantity={quantity}
              onQuantityChange={onQuantityChange ?? (() => {})}
              onBuy={onBuy ?? (() => {})}
              unitPrice={unitPrice}
              {...(maxForPurchaseCount !== undefined ? { maxQty: maxForPurchaseCount } : {})}
              {...(unavailableLabel ? { unavailableLabel } : {})}
            />
          </div>

          {/* Help — desktop only, directly under the RIP rail. The wrapper
              goes `contents` (not `block`) at ≥800px so the anchor itself
              becomes the aside's flex item: with the operator's knob off the
              anchor is `display:none` and drops out of the flex flow
              entirely, where a wrapper box would still have claimed one
              `gap-2` step under the rail. */}
          {/* `mr-3` = the RIP disc's own `right-[12px]` inside the rail, so
              the two right edges line up exactly. */}
          <div className="hidden @[800px]:contents">
            <HelpLink className="self-end mr-3" />
          </div>
        </aside>

        {/* DESKTOP-ONLY JustPulled — Figma 543:2216. Glass panel with
            header + marquee of recent pulls, scoped to the currently
            active pack. Shared with classic — the visual treatment is
            identical, so we reuse the same component instead of
            duplicating it under `templates/modern`. */}
        <div className="hidden @[800px]:block @[800px]:col-start-1 @[800px]:row-start-2">
          <JustPulled
            items={justPulledItems}
            loading={justPulledLoading}
            {...(onShowJustPulled ? { onShowAll: onShowJustPulled } : {})}
          />
        </div>

        {/* PotentialHits — visible on every breakpoint, matches classic.
            Mobile flow renders it after the slider / OddsPanel; desktop
            grid pins it to left column row 3. Reuses the shared
            component (visual identical between classic and modern). */}
        <div
          ref={potentialHitsRef}
          className="px-4 @[800px]:px-0 @[800px]:col-start-1 @[800px]:row-start-3 scroll-mt-3"
        >
          <PotentialHits
            items={potentialHits}
            loading={potentialHitsLoading}
          />
        </div>

        {/* Help — mobile only: the end of the scroll, under Potential Hits.
            Desktop has it in the sidebar under the RIP rail instead, where
            the mobile rail's fixed position leaves no room for it. */}
        <div className="contents @[800px]:hidden">
          <HelpLink className="justify-self-center" />
        </div>

        {/* Mobile spacer — reserves 156px (the RIP disc's reach: 16px
            bottom offset + 140px disc) under the in-flow content so it
            never slides under the portal'd `MobileBar` (which lives in
            `<body>` and is fixed to the viewport bottom). The bar itself
            is rendered exactly once from the desktop `<BottomAction>`
            mount inside the sidebar — its `MobileBar` child portals to
            body globally, so a second mount here would create a
            duplicate rail in `<body>`. */}
        <div
          className="@[800px]:hidden"
          style={{ height: MOBILE_RAIL_SPACER_HEIGHT }}
          aria-hidden
        />
      </div>
    </div>
  );
}
